import { ACCOUNT_HISTORY_TYPE, ACCOUNT_STATUS, TypeIdSchema, accountHistory } from '@unipile/db';
import { Static, Type } from '@sinclair/typebox';
import { StringEnum } from '../../utils/typebox';

export const AccountHistoryTypeSchema = StringEnum([...ACCOUNT_HISTORY_TYPE], {
  description: `The kind of change recorded in the account history.
    - \`status_change\` is a transition of the account status.
    - \`proxy_swap\` is a change of the proxy attached to the account.
    - \`account_scope_assignment\` is the permanent assignment of a Scope.`,
});

export const PublicAccountHistoryEntrySchema = Type.Object(
  {
    object: Type.Literal('AccountHistoryEntry'),
    id: TypeIdSchema('accountHistory', { description: 'ID of the history entry.' }),
    type: AccountHistoryTypeSchema,
    created_at: Type.String({ description: 'The date and time the change occurred.' }),
    new_status: Type.Optional(
      StringEnum([...ACCOUNT_STATUS], { description: 'The new account status, for `status_change` entries.' }),
    ),
    new_proxy: Type.Optional(Type.String({ description: 'A readable label for the new proxy, for `proxy_swap` entries.' })),
    new_account_scope_id: Type.Optional(
      TypeIdSchema('accountScope', { description: 'The assigned Scope, for `account_scope_assignment` entries.' }),
    ),
    reason: Type.Optional(Type.String({ description: 'A readable reason for the change, when available.' })),
    product: Type.Optional(
      Type.String({ description: 'The product scope of the status change, when the transition is product-specific.' }),
    ),
  },
  { title: 'AccountHistoryEntry' },
);

export const ListAccountHistoryResponseSchema = Type.Object({
  object: Type.Literal('AccountHistory'),
  data: Type.Array(PublicAccountHistoryEntrySchema),
  has_more: Type.Boolean({ description: 'Whether there are more history entries to fetch.' }),
});

export const ListAccountHistoryQuerySchema = Type.Object({
  offset: Type.Optional(Type.String({ description: 'An offset used for pagination.' })),
  limit: Type.Optional(Type.String({ description: 'The limit of items to be returned.', default: '20' })),
});

export type AccountHistoryEntryRecord = typeof accountHistory.$inferSelect;
export type PublicAccountHistoryEntry = Static<typeof PublicAccountHistoryEntrySchema>;
export type ListAccountHistoryResponse = Static<typeof ListAccountHistoryResponseSchema>;
